/**
 * The one path editor (tasks 385 + 388).
 *
 * Before this component the app had three path editors with three different
 * behaviours: the create form (required-only validation), the detail page's
 * inline editor (silent `return` on empty input or a missing leading slash — the
 * User clicked save and nothing happened, with no error anywhere), and the bulk
 * editor (no validation at all). They are all this component now, so there is
 * exactly one contract:
 *
 * 1. **Required.** Empty is an error the User can see, never a silent no-op.
 * 2. **Normalized.** Leading/trailing slashes are stripped before submit — the
 *    server strips them too, so storing the bare form keeps the app's display
 *    and the stored value identical.
 * 3. **Visible.** Every rejection renders inline, wired to the input via
 *    `aria-describedby`/`aria-invalid`.
 *
 * The fixed `/api/{accountSlug}/` prefix in front of the field is the task-385
 * half: the account slug namespaces the URL, so the User types only their own
 * path segment and can see the full callable URL as they type. The prefix takes
 * the CALLER's slug (`useAccountSlug()`) when editing/creating in one's own
 * account, or an explicit `accountSlug` when the row's owner is known.
 *
 * Server-side rules deliberately stay server-side: a reserved first segment
 * (422) and a duplicate path within the account (409) are reported by the API
 * and surfaced by the calling form through `error` — this component never tries
 * to predict them.
 */
import { useId } from 'react';
import { useAccountSlug } from '@/contexts/AuthContext';

/** Strip surrounding whitespace and any leading/trailing slashes. */
export function normalizeEndpointPathValue(value: string): string {
  return value.trim().replace(/^\/+/, '').replace(/\/+$/, '');
}

/**
 * Validate a path as typed. Returns an error message, or null when acceptable.
 * Intentionally minimal — it rejects only what the User can see and fix locally;
 * ownership and reservation rules belong to the server.
 */
export function validateEndpointPath(value: string): string | null {
  const normalized = normalizeEndpointPathValue(value);
  if (!normalized) return 'Path is required';
  if (/\s/.test(normalized)) return 'Path cannot contain spaces';
  if (/[?#]/.test(normalized)) return 'Path cannot contain ? or #';
  return null;
}

interface EndpointPathInputProps {
  /** Current raw value as typed (not normalized — normalize on submit). */
  value: string;
  onChange: (value: string) => void;
  /**
   * Owning account slug for the prefix. Omit to use the caller's own slug,
   * which is the right default for both create and self-owned edits.
   */
  accountSlug?: string | null;
  /** Inline error to display (local validation or a surfaced API error). */
  error?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  name?: string;
  /** Dense variant for the detail page's inline and bulk editors. */
  compact?: boolean;
  /** Hide the label (the surrounding layout already provides one). */
  hideLabel?: boolean;
  /** Suppress the explanatory help line (dense contexts). */
  hideHelpText?: boolean;
}

export function EndpointPathInput({
  value,
  onChange,
  accountSlug,
  error,
  onKeyDown,
  onBlur,
  disabled = false,
  autoFocus = false,
  name = 'path',
  compact = false,
  hideLabel = false,
  hideHelpText = false,
}: EndpointPathInputProps) {
  const callerSlug = useAccountSlug();
  // `accountSlug === undefined` means "not specified, use mine"; an explicit
  // null means "this row has no slug" (a legacy endpoint) and must not silently
  // borrow the caller's.
  const slug = accountSlug === undefined ? callerSlug : accountSlug;
  const reactId = useId();
  const inputId = `endpoint-path-${reactId}`;
  const errorId = `${inputId}-error`;
  const helpId = `${inputId}-help`;

  const prefix = slug ? `/api/${slug}/` : '/api/';
  const describedBy = [error ? errorId : null, !hideHelpText ? helpId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div>
      {!hideLabel && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Path <span className="text-red-500">*</span>
        </label>
      )}
      <div
        className={`flex items-stretch rounded-md border shadow-sm overflow-hidden focus-within:ring-1 focus-within:ring-indigo-500 focus-within:border-indigo-500 ${
          error
            ? 'border-red-500 dark:border-red-500'
            : 'border-gray-300 dark:border-gray-600'
        } ${disabled ? 'opacity-60' : ''}`}
      >
        <span
          data-testid="endpoint-path-prefix"
          title={
            slug
              ? `Your account namespace — every endpoint you create is served under /api/${slug}/`
              : 'Endpoints are served under /api/'
          }
          className={`flex select-none items-center whitespace-nowrap border-r border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 font-mono text-gray-500 dark:text-gray-400 ${
            compact ? 'px-2 text-xs' : 'px-3 text-sm'
          }`}
        >
          {prefix}
        </span>
        <input
          id={inputId}
          name={name}
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-label={hideLabel ? 'Path' : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          placeholder="my-service"
          className={`w-full min-w-0 flex-1 border-0 bg-white dark:bg-gray-700 font-mono text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 ${
            compact ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'
          }`}
        />
      </div>
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      {!hideHelpText && (
        <p id={helpId} className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Type only your path — the account prefix is added for you. Paths must be
          unique within your account.
        </p>
      )}
    </div>
  );
}

export default EndpointPathInput;
